import { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../../context/AuthContext";

const CHAT_URL = "https://techfest.org/api/chat/";

// Cleans up mojibake (mis-decoded UTF-8 -> Latin-1 artifacts) and
// strips citation markers like [0], [1], [12] from streamed text.
function sanitizeText(text) {
    if (!text) return text;

    return (
        text
            // Common mis-decoded punctuation sequences
            .replace(/â€™/g, "\u2019") // ’
            .replace(/â€˜/g, "\u2018") // ‘
            .replace(/â€œ/g, "\u201c") // “
            .replace(/â€\u009d/g, "\u201d") // ” (some servers send this variant)
            .replace(/â€\?/g, "\u201d") // ” fallback
            .replace(/â€"/g, "\u2013") // –
            .replace(/â€"/g, "\u2014") // —
            .replace(/â€¦/g, "\u2026") // …
            .replace(/â€¢/g, "\u2022") // •
            .replace(/Â°/g, "\u00b0") // °
            .replace(/Â®/g, "\u00ae") // ®
            .replace(/Â©/g, "\u00a9") // ©
            .replace(/Â /g, " ")
            .replace(/Â/g, "")
            // Any remaining stray "â" + junk byte sequences we haven't named
            .replace(/â[\u0080-\u00BF]{1,2}/g, "")
            // Citation markers like [0], [1], [23]
            .replace(/\[\d+\]/g, "")
            // Collapse any double spaces left behind by the removals above
            .replace(/[ \t]{2,}/g, " ")
            .trim()
    );
}

const styles = `
  .tf-chatbot-widget {
    display: flex;
    flex-direction: column;
    width: 360px;
    height: 520px;
    border-radius: 12px;
    border: 1px solid rgba(79, 172, 254, 0.25);
    background: linear-gradient(180deg, #0f1115 0%, #0a0b0d 100%);
    color: #eaeaea;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    overflow: hidden;
    position: relative;
  }

  .tf-chatbot-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.2px;
    background: linear-gradient(135deg, rgba(47,111,237,0.25), rgba(79,172,254,0.08));
    border-bottom: 1px solid rgba(79, 172, 254, 0.2);
    position: relative;
    backdrop-filter: blur(6px);
  }

  .tf-chatbot-header::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #4facfe, transparent);
    opacity: 0.8;
  }

  .tf-chatbot-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
    
  .tf-chatbot-close {
    background: none;
    border: none;
    color: #9aa4b2;
    font-size: 14px;
    cursor: pointer;
    padding: 4px 7px;
    border-radius: 6px;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .tf-chatbot-close:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }

  .tf-chatbot-messages {
    flex: 1;
    overflow-y: auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: rgba(79,172,254,0.35) transparent;
  }

  .tf-chatbot-messages::-webkit-scrollbar {
    width: 6px;
  }

  .tf-chatbot-messages::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.35);
    border-radius: 10px;
  }

  .tf-chatbot-empty {
    margin: auto;
    color: #6b7280;
    font-size: 14px;
    text-align: center;
    line-height: 1.5;
  }

  .tf-chatbot-msg {
    display: flex;
    max-width: 85%;
    animation: tfMsgIn 0.25s ease-out;
  }

  @keyframes tfMsgIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .tf-chatbot-msg--user {
    align-self: flex-end;
  }

  .tf-chatbot-msg--bot {
    align-self: flex-start;
  }

  .tf-chatbot-bubble {
    padding: 10px 14px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.5;
    word-wrap: break-word;
  }

  .tf-chatbot-msg--user .tf-chatbot-bubble {
    background: linear-gradient(135deg, #2f6fed, #4facfe);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 10px rgba(47, 111, 237, 0.35);
  }

  .tf-chatbot-msg--bot .tf-chatbot-bubble {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px);
    border-bottom-left-radius: 4px;
  }

  .tf-chatbot-bubble p {
    margin: 0 0 6px 0;
  }

  .tf-chatbot-bubble p:last-child {
    margin-bottom: 0;
  }

  .tf-chatbot-sources {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 11px;
    color: #8a93a3;
  }

  .tf-chatbot-error {
    padding: 8px 14px;
    font-size: 13px;
    color: #ff9494;
    background: rgba(255, 80, 80, 0.08);
    border-top: 1px solid rgba(255, 80, 80, 0.25);
  }

  .tf-chatbot-input-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border-top: 1px solid rgba(79, 172, 254, 0.15);
    background: rgba(255, 255, 255, 0.02);
  }

  .tf-chatbot-input {
    flex: 1;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);
    color: #eaeaea;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .tf-chatbot-input::placeholder {
    color: #6b7280;
  }

  .tf-chatbot-input:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.15);
  }

  .tf-chatbot-send {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, #2f6fed, #4facfe);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(47, 111, 237, 0.4);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  .tf-chatbot-send:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(47, 111, 237, 0.55);
  }

  .tf-chatbot-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function Chatbot({ onClose }) {
    const { currentUser, authFetch } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const sessionId = useRef(null);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        if (!currentUser) {
            setError("Please sign in to use the chatbot.");
            return;
        }

        setMessages((prev) => [...prev, { role: "user", text }]);
        setInput("");
        setLoading(true);
        setError("");

        let botText = "";
        setMessages((prev) => [
            ...prev,
            { role: "bot", text: "", sources: [] },
        ]);

        try {
            const res = await authFetch(CHAT_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: text,
                    session_id: sessionId.current,
                }),
            });

            if (res.status === 401) throw new Error("UNAUTHORIZED");
            if (res.status === 429) throw new Error("RATE_LIMITED");
            if (!res.ok) throw new Error("REQUEST_FAILED");

            const newSessionId = res.headers.get("X-Session-Id");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                const events = buffer.split("\n\n");
                buffer = events.pop();

                for (const evt of events) {
                    const line = evt.replace(/^data:\s*/, "");
                    if (!line) continue;

                    let parsed;
                    try {
                        parsed = JSON.parse(line);
                    } catch {
                        continue;
                    }

                    if (parsed.type === "token") {
                        botText += parsed.text;
                        const cleaned = sanitizeText(botText);
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: "bot",
                                text: cleaned,
                                sources: [],
                            };
                            return updated;
                        });
                    } else if (parsed.type === "done") {
                        const cleaned = sanitizeText(botText);
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: "bot",
                                text: cleaned,
                                sources: parsed.sources || [],
                            };
                            return updated;
                        });
                    } else if (parsed.type === "error") {
                        throw new Error(parsed.message || "UPSTREAM_ERROR");
                    }
                }
            }

            if (newSessionId) sessionId.current = newSessionId;
        } catch (err) {
            if (err.message === "RATE_LIMITED") {
                setError(
                    "You've hit today's message limit — please try again tomorrow.",
                );
            } else if (err.message === "UNAUTHORIZED") {
                setError("Please sign in to use the chatbot.");
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tf-chatbot-widget">
            <style>{styles}</style>

            <div className="tf-chatbot-header">
                <div className="tf-chatbot-header-title">Ask XOR</div>
                {onClose && (
                    <button
                        className="tf-chatbot-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className="tf-chatbot-messages">
                {messages.length === 0 && (
                    <div className="tf-chatbot-empty">Powered by XYNE</div>
                )}
                {messages.map((m, i) => (
                    <div
                        key={i}
                        className={`tf-chatbot-msg tf-chatbot-msg--${m.role}`}
                    >
                        <div className="tf-chatbot-bubble">
                            <ReactMarkdown>{m.text || "…"}</ReactMarkdown>
                            {/* {m.sources && m.sources.length > 0 && (
                                <div className="tf-chatbot-sources">
                                    Sources: {m.sources.join(", ")}
                                </div>
                            )} */}
                        </div>
                    </div>
                ))}
            </div>

            {error && <div className="tf-chatbot-error">{error}</div>}

            <div className="tf-chatbot-input-row">
                <input
                    className="tf-chatbot-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Ask about Techfest..."
                    disabled={loading}
                />
                <button
                    className="tf-chatbot-send"
                    onClick={sendMessage}
                    disabled={loading}
                    aria-label="Send message"
                >
                    {loading ? "…" : "➤"}
                </button>
            </div>
        </div>
    );
}
